import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, IconButton } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CustomAvatar from "components/common/Avatar";
import Button from "components/common/Button";
import PageLoader from "components/common/PageLoader";
import { getEmployeesWithClockInStatus } from "api/clockInWorkforce";
import { baseUrl } from "util/APIBaseUrl";
import WorkforceModal from "../WorkforceModal/WorkforceModal";

import "./WorkforceToday.scss";

const STATUS_CONFIG = {
  clocked_in: { color: "#06B500", label: "Available" },
  on_break: { color: "#EB730E", label: "On break" },
  clocked_out: { color: "#939393", label: "Out of office" },
  on_leave: { color: "#939393", label: "On leave" },
  not_activated: { color: "#939393", label: "Clock in not enabled" },
  not_clocked_in: { color: "#939393", label: "Not clocked in" },
  no_shift: { color: "#939393", label: "Shift not selected" },
};

function WorkforceToday({ customerId }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, [customerId]);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getEmployeesWithClockInStatus(customerId, {
        page: 1,
        limit: 5,
      });

      if (response?.success) {
        setEmployees(response.data?.rows || []);
        setTotalCount(response.data?.count || 0);
      } else if (response?.rows) {
        setEmployees(response.rows || []);
        setTotalCount(response.count || 0);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error fetching workforce:", err);
      const message = err?.response?.data?.message || err?.message || "Failed to load workforce data";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  if (loading && employees.length === 0) {
    return (
      <Box className="workforce-today-widget">
        <div className="workforce-today-header">
          <p className="heading-h5-semibold">Your workforce today</p>
        </div>
        <div className="workforce-today-loading">
          <PageLoader />
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="workforce-today-widget">
        <div className="workforce-today-header">
          <p className="heading-h5-semibold">Your workforce today</p>
        </div>
        <div className="workforce-today-error">
          <p className="para-body-s-regular text-error">{error}</p>
        </div>
      </Box>
    );
  }

  if (employees.length === 0) {
    return (
      <Box className="workforce-today-widget">
        <div className="workforce-today-header">
          <p className="heading-h5-semibold">Your workforce today</p>
        </div>
        <div className="workforce-today-empty">
          <p className="para-body-s-regular text-headings-secondary">No employees found</p>
        </div>
      </Box>
    );
  }

  return (
    <>
      <Box className="workforce-today-widget">
        <div className="workforce-today-header">
          <p className="heading-h5-semibold">Your workforce today ({totalCount})</p>
          <Button
            label="View all"
            variant="text"
            color="primary"
            size="small"
            onClick={handleViewAll}
            className="workforce-view-all-btn"
          />
        </div>

        <div className="workforce-today-list">
          {employees.map((employee) => {
            const { clockInStatus } = employee;
            const isEnabled = clockInStatus?.isEnabled;
            const status = clockInStatus?.status;
            const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.no_shift;
            const statusMessage = isEnabled
              ? clockInStatus?.statusMessage || statusConfig.label
              : "Clock in/out not activated";

            return (
              <div key={employee.employeeId} className="workforce-employee-item">
                <div className="workforce-employee-avatar-wrapper">
                  <CustomAvatar
                    src={
                      employee.pictureVersion
                        ? `${baseUrl}/employee/${employee.employeeId}/logo?id=${employee.pictureVersion}`
                        : ""
                    }
                    alt={`${employee.firstName} ${employee.lastName}`}
                    size="md"
                  />
                  {isEnabled && (
                    <span className="workforce-status-badge" style={{ backgroundColor: statusConfig.color }} />
                  )}
                </div>

                <p className="heading-h5-medium workforce-employee-name" style={{ flex: 1 }}>
                  {employee.firstName} {employee.lastName}
                </p>

                <div className="workforce-employee-status">
                  <p className="para-body-s-regular text-headings-secondary">{statusMessage}</p>
                </div>

                <IconButton
                  size="small"
                  className="workforce-employee-chevron"
                  onClick={() => navigate(`/main/details/${employee.employeeId}#timesheet`)}
                >
                  <ChevronRightIcon />
                </IconButton>
              </div>
            );
          })}
        </div>
      </Box>

      {showModal && <WorkforceModal open={showModal} onClose={handleCloseModal} customerId={customerId} />}
    </>
  );
}

WorkforceToday.propTypes = {
  customerId: PropTypes.string.isRequired,
};

export default WorkforceToday;
